import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import { isEmpty } from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@utx/utx-access-client';
import { MobileNo } from '@/features/account-management/types';
import { phoneNumberProp } from '@/features/account-management/screens/details/hooks/useCard.hook';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { UpdateMobileNoStackParamList, UpdateMobileNoStackRoutes } from '@/features/account-management/screens/update-mobile-no/type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';

export interface UpdateMobileNoProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: MobileNo;
  func?: (cardId: string, phoneNumber: phoneNumberProp, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  onChangeView?: () => void;
  record?: Card;
}

export const UpdateMobileNoWebOneScreen: React.FC<UpdateMobileNoProps> = (props) => {
  const { onClose, onLayout, data, func, onReturnData, refresh, onChangeView, record } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, textMb, textTitle, textAtmPin, btnView, btn } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<UpdateMobileNoStackParamList>>();
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        title={t('card-account-management::replace-card.title--update-mobile-number-confirmation')}
        closeModal={() => {
          navigation?.goBack();
          onChangeView && onChangeView();
        }}
      />
      <View sx={body}>
        <TextBody sx={textMb} semiBold>
          {t('card-account-management::replace-card.text--update-mobile-number-inquiry')}
        </TextBody>
        <TextCaption sx={textTitle} semiBold>
          {t('card-account-management::replace-card.text--new-mobile-number')}
        </TextCaption>
        <H6 sx={textAtmPin}>
          +{data?.phoneCode} {data?.phoneNumber}
        </H6>
        {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
        <View sx={btnView}>
          <UniButton
            sx={btn}
            disabled={isLoading}
            variant={ButtonVariant.outline1}
            label={t('card-account-management::general.button--cancel')}
            onPress={() => navigation.getParent()?.goBack()}
          />
          <UniButton
            sx={btn}
            label={t('card-account-management::general.button--place-instruction')}
            loading={isLoading}
            onPress={() => {
              setIsLoading(true);
              func &&
                func(
                  record?.id!,
                  { countryCode: data?.phoneCode!, mobileNumber: data?.phoneNumber! },
                  (returnData) => {
                    setIsLoading(false);
                    onReturnData && onReturnData(returnData.data);
                    navigation.navigate(UpdateMobileNoStackRoutes.Placed, {
                      record,
                      onLayout: onLayout,
                      data,
                      returnData: returnData.data,
                      refresh
                    });
                  },
                  (error) => {
                    setIsLoading(false);
                    setAlertMessage(error?.message);
                  }
                );
            }}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40
  },
  btn: {
    width: 216,
    height: 44
  }
};
